import { computed, DestroyRef, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, Validators } from '@angular/forms';
import { catchError, filter, map, Observable, of } from 'rxjs';
import { IEvent } from '@app/api/interfaces/event.interface';
import { DynamicDialogConfig } from 'primeng/dynamicdialog';
import { LdEventInvestigationResult } from '@app/main/leak-detection/enums/ld-event-investigation-result.enum';
import { isLeakFoundResult } from '@app/main/leak-detection/utils/ld-event-status';
import { IDialogEventData } from '@app/main/leak-detection/dialogs/dialog-event/interfaces/dialog-event-data.interface';
import { IDialogEventDraft } from '@app/main/leak-detection/dialogs/dialog-event/interfaces/dialog-event-draft.interface';
import {
  isSameDraft,
  needsAssignee,
  toDraft,
} from '@app/main/leak-detection/dialogs/dialog-event/utils/dialog-event-draft';
import { dialogEventDraftValidator } from '@app/main/leak-detection/dialogs/dialog-event/validators/dialog-event-draft.validator';
import { DialogEventPersistenceService } from '@app/main/leak-detection/dialogs/dialog-event/services/dialog-event-persistence.service';
import { AddressesApiService } from '@app/api/services/addresses-api.service';
import { DIALOG_EVENT_MAX_COMMENT_LENGTH } from '@app/main/leak-detection/dialogs/dialog-event/configs/dialog-event-comment.config';

@Injectable()
export class DialogEventState {
  private readonly data = inject<DynamicDialogConfig<IDialogEventData>>(DynamicDialogConfig).data!;
  private readonly fb = inject(FormBuilder);
  private readonly persistence = inject(DialogEventPersistenceService);
  private readonly addressesApi = inject(AddressesApiService);
  private readonly destroyRef = inject(DestroyRef);

  /** null while creating — there is no event behind the popup yet */
  private readonly originalEvent = signal<IEvent | null>(this.data.event);

  public readonly event = this.originalEvent.asReadonly();

  public readonly form = this.fb.nonNullable.group(toDraft(this.data.event, this.data.position), {
    validators: dialogEventDraftValidator,
  });

  /** the note saved along with the draft — a created event's first history entry */
  public readonly commentForm = this.fb.nonNullable.group({
    comment: ['', Validators.maxLength(DIALOG_EVENT_MAX_COMMENT_LENGTH)],
  });

  private readonly savedDraft = signal<IDialogEventDraft>(this.form.getRawValue());

  public readonly draft = toSignal(this.form.valueChanges.pipe(map(() => this.form.getRawValue())), {
    initialValue: this.form.getRawValue(),
  });

  private readonly comment = toSignal(this.commentForm.controls.comment.valueChanges, { initialValue: '' });

  public readonly isSaving = signal(false);

  public readonly isCreating = computed(() => !this.originalEvent());

  public readonly status = computed(() => this.draft().status);

  public readonly isHidden = computed(() => this.draft().hidden);

  public readonly investigationResult = computed(() => this.draft().investigationResult);

  public readonly isResolvedAsNoLeak = computed(() => this.investigationResult() === LdEventInvestigationResult.NoLeak);

  public readonly isResolvedAsLeakFound = computed(() => isLeakFoundResult(this.investigationResult()));

  public readonly repairStatus = computed(() => (this.isResolvedAsLeakFound() ? this.draft().repairStatus : null));

  public readonly isDirty = computed(() => !isSameDraft(this.draft(), this.savedDraft()) || !!this.comment().trim());

  /** null until the event is changed after it was created — its creation is history's initial entry */
  public readonly updatedAt = computed(() => {
    const entry = this.originalEvent()?.lastHistoryEntry;

    return entry && !entry.initial ? entry.performedAt : null;
  });

  constructor() {
    const { assignedTo } = this.form.controls;

    assignedTo.valueChanges
      .pipe(
        filter(() => assignedTo.dirty),
        takeUntilDestroyed()
      )
      .subscribe(() => this.saveAssignee());

    if (!this.data.event) this.resolveAddress();
  }

  private resolveAddress(): void {
    const { coordinates, location } = this.draft();

    if (!coordinates || location) return;

    this.addressesApi
      .getByCoordinates(coordinates)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(response => {
        const address = response.data[0]?.address;

        if (!address || this.form.controls.location.value) return;

        this.form.controls.location.setValue(address);
        this.savedDraft.update(draft => ({ ...draft, location: address }));
      });
  }

  // --- immediate writes: the tabs save these on their own, apart from the rest of the draft ------

  public addComment(content: string): void {
    const event = this.originalEvent();
    const trimmed = content.trim();

    if (!event || !trimmed) return;

    this.commit(this.persistence.addComment(event.id, trimmed)).subscribe();
  }

  public setHidden(hidden: boolean): void {
    const event = this.originalEvent();

    if (!event) return;

    const { hidden: control } = this.form.controls;

    control.setValue(hidden);

    // a failed write puts the field back to what the server holds — nothing is left to read as unsaved
    this.commit(this.persistence.setHidden(event.id, hidden)).subscribe(saved => {
      if (!saved) control.setValue(this.savedDraft().hidden);
    });
  }

  // --- footer ------------------------------------------------------------------------------------

  public save(): Observable<boolean> {
    if (this.form.invalid || this.commentForm.invalid) {
      this.form.markAllAsTouched();

      return of(false);
    }

    return this.saveDraft(this.form.getRawValue(), this.commentForm.controls.comment.value);
  }

  /**
   * Saves `draft` — the editors hand theirs in here once their own form is valid. The form takes it
   * only once saved, from the server's answer: a failed save leaves the event as it was, and the
   * editor open to try again.
   */
  public saveDraft(draft: IDialogEventDraft, comment = ''): Observable<boolean> {
    const event = this.originalEvent();
    const note = comment.trim() || undefined;

    return this.commit(event ? this.persistence.save(event, draft, note) : this.persistence.create(draft, note), draft);
  }

  public discard(): void {
    this.form.reset(this.savedDraft());
    this.commentForm.reset();
  }

  // --- write plumbing ----------------------------------------------------------------------------

  private saveAssignee(): void {
    const event = this.originalEvent();
    const { assignedTo } = this.form.controls;

    if (!event) return;

    // an Investigation or Resolved event keeps someone on it
    if (needsAssignee(this.status()) && !assignedTo.value) return;

    // handed over — pristine again, so a failed save cannot be re-sent by a later write of the form
    assignedTo.markAsPristine();

    // put back on failure, as `setHidden` does; pristine, so putting it back is not saved in turn
    this.commit(this.persistence.saveAssignee(event.id, assignedTo.value)).subscribe(saved => {
      if (!saved) assignedTo.setValue(this.savedDraft().assignedTo);
    });
  }

  private commit(write$: Observable<IEvent>, sent: IDialogEventDraft = this.form.getRawValue()): Observable<boolean> {
    this.isSaving.set(true);

    return write$.pipe(
      map(event => {
        this.adopt(event, sent);

        return true;
      }),
      catchError(() => {
        this.isSaving.set(false);

        return of(false);
      }),
      takeUntilDestroyed(this.destroyRef)
    );
  }

  private adopt(event: IEvent, { pressureUnit, leakSizeUnit }: IDialogEventDraft): void {
    const saved = { ...toDraft(event, event.coordinates), pressureUnit, leakSizeUnit };

    this.originalEvent.set(event);
    this.savedDraft.set(saved);
    this.form.reset(saved);
    this.commentForm.reset();
    this.isSaving.set(false);
  }
}
